<template>
  <vxe-grid :scroll-x="scrollX"
            :scroll-y="scrollY"
            v-bind="$attrs"
            v-on="new$listeners"
            ref="vxeGrid"
            :columns="columns"
            :column-key="columnKey"
            :keep-source="keepSource"
            :show-overflow="showOverflow"
            class="x-table"
            :keyboard-config="realKeyboardConfig"
            :footer-method="realFooterMethod"
            :radio-config="realRadioConfig"
            :checkbox-config="realCheckboxConfig"
            @filter-change="handleFilterChange"
            @custom="handleCustomChange"
            @menu-click="handleMenuClick"
            @resizable-change="handleResizableChange"
            :sort-config="realSortConfig">
    <template v-for="(item) in slotArray" v-slot:[item]="scope">
      <slot :name="item" v-bind="scope"></slot>
    </template>
  </vxe-grid>
</template>

<script>
import {Grid} from 'vxe-table';
import tableSettingMixin from '../../mixin/table-setting-mixin';
import tableSettingConfigMixin from '../../mixin/table-setting-config-mixin';
import {EXPAND_CHANGE, SIDE_BAR_LOCK_CHANGE, WINDOW_RESIZE, ROOT_DIALOG_RESIZE_EVENT_NAME} from '../../util/eventKey';
import {convertSortList, countSingleColumn, setSortNumber, setTabindex, initCloumnHeaderSort} from '../../util/componentUtil';
import * as appUtils from '../../util/appUtils';
import XEUtils from 'xe-utils';
// import {defaultRemote} from '@/util/log';
function getSlotArray(array, columns) {
  if (!Array.isArray(columns)) {
    return;
  }
  for (const item of columns) {
    if (item.children) {
      getSlotArray(array, item.children);
    }
    if (item.slots) {
      for (const key in item.slots) {
        const slot = item.slots[key];
        if ((typeof slot).toLowerCase() === 'string' || slot instanceof String) {
          array.push(slot);
        }
      }
    }
  }
}

function footerMethod({columns, data}) {
  return [
    columns.map((column, columnIndex) => {
      if (columnIndex === 0) {
        return '合计';
      }
      const params = column.params;
      if (!params || !params.showFooterCount) {
        return null;
      }
      return countSingleColumn({column, rows: data});
    })
  ];
}
export default {
  name: 'XTable',
  components: {
    [Grid.name]: Grid
  },
  mixins: [tableSettingConfigMixin, tableSettingMixin], // 表格变化保存到服务器
  inheritAttrs: false,
  props: {
    scrollX: {
      type: Object,
      default() {
        return {enabled: false};
      }
    },
    scrollY: {
      type: Object,
      default() {
        return {enabled: false};
      }
    },
    keepSource: {
      type: Boolean,
      default: true
    },
    columns: {
      type: Array,
      default() {
        return [];
      }
    },
    showOverflow: {
      type: [Boolean, String],
      default: 'tooltip'
    },
    keyboardConfig: Object,
    footerMethod: Function,
    radioConfig: Object,
    checkboxConfig: Object,
    sortConfig: Object,
    columnKey: {
      type: Boolean,
      default: true
    }
  },
  mounted() {
    // 初始化sortList
    this.sortList = [];
    // 由于sortMethod的执行早于sort-change，因此需要在sortMethod中处理sortList的顺序。
    // 当remote为true时，sortMethod不执行，此时由sortChange处理
    if (!this.addListListenerFlag) {
      this.$root.$on(ROOT_DIALOG_RESIZE_EVENT_NAME, this.handleWindowResize);
      // 监听大小变化，然后重新渲染列表
      // 由于vxe-table本身就监听window大小的改变，所以这里不需要重复监听,如果还是出现掉帧问题，这里就需要增加判断
      this.$root.$on(WINDOW_RESIZE, this.handleWindowResize);
      this.$root.$on(SIDE_BAR_LOCK_CHANGE, this.handleSidebarLockChange);
      this.$root.$on(EXPAND_CHANGE, this.handleExpandChange);
      this.addListListenerFlag = true;
    }
  },
  activated() {
    if (this.$windowResize) {
      this.$windowResize = false;
      this.getGridInstance().recalculate(true);
    }
  },
  deactivated() {
    this.getGridInstance().closeTooltip();
  },
  methods: {
    getSortList() {
      return this.sortList;
    },
    sort (sortConfs, sortOrder) {
      const grid = this.getGridInstance();
      if (!sortConfs) {
        return grid.sort(sortConfs, sortOrder);
      }
      // 清空原有的表头的排序数字
      setSortNumber({sortList: this.sortList, clear: true});
      const multiple = grid.sortConfig.multiple;
      // 如果传入的sortConfs是一个字符串就要先找出对应的column
      if (appUtils.isString(sortConfs)) {
        // 查找对应的column
        const column = grid.getColumnByField(sortConfs);
        if (multiple) {
          // 如果是多列排序，就要找到column在sortList中的位置，并替换order,否则就在后面追加
          let flag = false;
          for (const item of this.sortList) {
            if (item.field === column.field) {
              flag = true;
              item.order = sortOrder;
              break;
            }
          }
          if (!flag) {
            this.sortList.push({column, field: column.field, order: sortOrder});
          }
        } else {
          this.sortList = [{column, field: column.field, order: sortOrder}];
        }
        setSortNumber({sortList: this.sortList});
        return grid.sort(sortConfs, sortOrder);
      }
      let newSortConfs;
      // 如果传入的不是数组，就转成数组
      if (!Array.isArray(sortConfs)) {
        newSortConfs = [sortConfs];
      } else {
        newSortConfs = sortConfs;
      }
      if (multiple) {
        // 多列排序，就要判断每个列是否已经在this.sortList中，如果是替换order,不是就在sortList后面追加
        for (const item of newSortConfs) {
          let column = item.field;
          if (appUtils.isString(column)) {
            column = grid.getColumnByField(column);
          }
          let flag = false;
          for (const sort of this.sortList) {
            if (sort.field === column.field) {
              sort.order = item.order;
              flag = true;
              break;
            }
          }
          if (!flag) {
            this.sortList.push({column, field: column.field, order: item.order});
          }
        }
      } else {
        // 不是多列排序，就用第一个列
        const firstSortConf = newSortConfs[0];
        let column = firstSortConf.field;
        if (appUtils.isString(column)) {
          column = grid.getColumnByField(column);
        }
        this.sortList = [{column, field: column.field, order: firstSortConf.order}];
      }
      setSortNumber({sortList: this.sortList});
      return grid.sort(sortConfs);
    },
    getRecords () {
      const grid = this.getGridInstance();
      const row = grid.getRadioRecord();
      if (row) {
        return [row];
      }
      return grid.getCheckboxRecords();
    },
    getMouseDownCaptureDom() {
      return this.getGridInstance().$el;
    },
    getKeyDownCaptureDom() {
      return setTabindex({element: this.getGridInstance().$el});
    },
    onKeyDownCapture(event) {
      this.$emit('keydown-capture', {$event: event, $grid: this.getGridInstance()});
    },
    onMouseDownCapture(event) {
      this.$emit('mousedown-capture', {$event: event, $grid: this.getGridInstance()});
    },
    handleSortChange (args) {
      const {sortList} = args;
      setSortNumber({sortList: this.sortList, clear: true});
      this.sortList = convertSortList({
        currentSortList: sortList,
        lastSortList: this.sortList
      });
      setSortNumber({sortList: this.sortList});
      // 如果父组件的模板中直接传入了sort-change，就不需要通过这个方法发射事件
      // 经测试发现，如果本组件或者父组件模板中没有传入sort-change，直接使用$on来监听，回调不会被调用，vxe-table应该是根据$listener来判断是否需要$emit
      // 以下拷贝vxe-table的源码
      // _xeUtils.default.each($listeners, function (cb, type) {
      //   ons[type] = function () {
      //     for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      //       args[_key] = arguments[_key];
      //     }
      //
      //     _vm.$emit.apply(_vm, [type].concat(args));
      //   };
      // });
      // 通过new$listeners来控制事件发射，使handleSortChange的逻辑比父组件监听的事件先执行
      // this.sortList是按照点击顺序来排列的，更符合使用的习惯
      args.originSortList = args.sortList;
      args.sortList = this.sortList;
      this.$emit('sort-change', args);
    },
    handleFilterChange (args) {
      if (this.$listeners['filter-change']) {
        return;
      }
      this.$emit('filter-change', args);
    },
    handleCustomChange (event) {
      if (event.type !== 'confirm' && event.type !== 'reset') {
        return;
      }
      setTimeout(() => {
        // 隐藏或者显示部分字段后，表头层级有可能发生变化，需要重新初始化
        this.initColumnDrag();
        this.saveTableColumnMsg();
        /*const loginUser = this.$access.getInfo();
        const info = {type: event.type};
        if (loginUser) {
          info.userId = loginUser.userId;
        }
        defaultRemote({
          url: window.location.href,
          frontAppName: this.$access.options.systemType || '',
          info: JSON.stringify(info)
        });*/
      }, 0);
    },
    recalculate(bool) {
      return this.getGridInstance().recalculate(bool);
    },
    reloadRow(rows, record, field) {
      this.getGridInstance().reloadRow(rows, record, field);
    },
    loadData(arr) {
      return this.getGridInstance().loadData(arr);
    },
    setActiveCell(row, fieldOrColumn) {
      return this.getGridInstance().setActiveCell(row, fieldOrColumn);
    },
    handleExpandChange() {
      if (this._inactive) {
        return;
      }
      this.$windowResize = false;
      this.getGridInstance().recalculate(true);
    },
    handleWindowResize($dialog) {
      if ($dialog) {
        this.getGridInstance().recalculate(true);
        return;
      }
      if (!this._inactive) {
        // 如果处于activated状态，会自动重新渲染，不需要调用调用重新计算的方法
        // 但是点击最大化按钮和还原按钮，不会重新计算，
        // 目前无法监听最大化和还原按钮的事件，因此这里仍然需要调用一次列表的计算
        this.getGridInstance().recalculate(true);
        this.$windowResize = false;
        return;
      }
      // 否则就等到activated方法调用时再重新计算
      this.$windowResize = true;
    },
    handleSidebarLockChange() {
      if (!this._inactive) {
        // 如果处于activated状态，就直接重新计算表格
        this.$windowResize = false;
        this.getGridInstance().recalculate(true);
        return;
      }
      this.$windowResize = true;
    },
    getScroll() {
      return this.getGridInstance().getScroll();
    },
    scrollTo(scrollLeft, scrollTop) {
      return this.getGridInstance().scrollTo(scrollLeft, scrollTop);
    },
    getColumns() {
      return this.getGridInstance().getColumns();
    },
    getOriginalColumns() {
      return this.columns;
    },
    loadColumn(columns) {
      return this.getGridInstance().loadColumn(columns);
    },
    getCheckboxRecords() {
      return this.getGridInstance().getCheckboxRecords();
    },
    removeCheckboxRow() {
      return this.getGridInstance().removeCheckboxRow();
    },
    getRadioRecord() {
      return this.getGridInstance().getRadioRecord();
    },
    getTableData() {
      return this.getGridInstance().getTableData();
    },
    getGridInstance() {
      const vxeGrid = this.$refs.vxeGrid;
      if (!vxeGrid) {
        throw new NonsenseError('vxeGrid is null');
      }
      return this.$refs.vxeGrid;
    },
    setActiveRow(row) {
      return this.getGridInstance().setActiveRow(row);
    },
    getTableColumn() {
      return this.getGridInstance().getTableColumn();
    },
    clearSort(fieldOrColumn) {
      const grid = this.getGridInstance();
      if (!fieldOrColumn) {
        for (const sortColumn of this.columns) {
          if (sortColumn.params) {
            delete sortColumn.params.sortNumber;
          }
        }
        this.sortList = [];
        return grid.clearSort(fieldOrColumn);
      }
      let column = fieldOrColumn;
      if (appUtils.isString(fieldOrColumn)) {
        column = grid.getColumnByField(fieldOrColumn);
      }
      for (let i = 0; i < this.sortList.length; i++) {
        if (this.sortList[i].field === column.field) {
          const sort = this.sortList.splice(i, 1)[0];
          const sortColumn = sort.column;
          if (sortColumn.params) {
            delete sortColumn.params.sortNumber;
          }
          break;
        }
      }
      setSortNumber({sortList: this.sortList});
      return grid.clearSort(fieldOrColumn);
    },
    handleResizableChange ({ column }) {
      setTimeout(() => {
        this.saveTableColumnMsg();
      }, 0);
    },
    getOrderField (_vm, column) {
      const { sortBy, sortType } = column;
      return (row) => {
        let cellValue;
        if (sortBy) {
          cellValue = XEUtils.isFunction(sortBy) ? sortBy({ row, column }) : XEUtils.get(row, sortBy);
        } else {
          cellValue = _vm.getCellLabel(row, column);
        }
        if (!sortType || sortType === 'auto') {
          return isNaN(cellValue) ? cellValue : XEUtils.toNumber(cellValue);
        } else if (sortType === 'number') {
          return XEUtils.toNumber(cellValue);
        } else if (sortType === 'string') {
          return XEUtils.toValueString(cellValue);
        }
        return cellValue;
      };
    }
  },
  computed: {
    realSortConfig() {
      const sortMethod = (args) => {
        const {data, sortList} = args;
        setSortNumber({sortList: this.sortList, clear: true});
        this.sortList = convertSortList({
          currentSortList: sortList,
          lastSortList: this.sortList
        });
        setSortNumber({sortList: this.sortList});
        return XEUtils.orderBy(data, this.sortList.map(({ column, order }) => [this.getOrderField(this.getGridInstance(), column), order]));
      };
      const defaultConfig = {sortMethod};
      if (this.sortConfig) {
        Object.assign(defaultConfig, this.sortConfig);
      }
      return defaultConfig;
    },
    new$listeners() {
      // 为了避免父组件监听子组件的方法被重复调用，替换了部分监听方法
      return Object.assign({}, this.$listeners, {
        'sort-change': this.handleSortChange
      });
    },
    realFooterMethod() {
      return this.footerMethod ? this.footerMethod : footerMethod;
    },
    realKeyboardConfig() {
      // isArrow必须要传，如果是undefined，调用setCellAreas时即使目标单元格在可见范围内都会导致滚动条滚动
      const defaultConfig = {isClip: true, isArrow: true, isEnter: true, isTab: true};
      if (this.keyboardConfig === undefined) {
        return defaultConfig;
      }
      if (this.keyboardConfig === null) {
        return null;
      }
      return Object.assign({}, defaultConfig, this.keyboardConfig);
    },
    slotArray() {
      const array = [];
      getSlotArray(array, this.columns);
      return array;
    },
    realRadioConfig() {
      const defaultConfig = {highlight: true};
      if (this.radioConfig) {
        Object.assign(defaultConfig, this.radioConfig);
      }
      return defaultConfig;
    },
    realCheckboxConfig() {
      const defaultConfig = {highlight: true};
      if (this.checkboxConfig) {
        Object.assign(defaultConfig, this.checkboxConfig);
      }
      return defaultConfig;
    }
  },
  watch: {
    columns: {
      handler: initCloumnHeaderSort,
      immediate: true
    }
  },
  beforeDestroy() {
    this.getGridInstance().closeTooltip();
    if (this.addListListenerFlag) {
      this.$root.$off(ROOT_DIALOG_RESIZE_EVENT_NAME, this.handleWindowResize);
      this.$root.$off(WINDOW_RESIZE, this.handleWindowResize);
      this.$root.$off(SIDE_BAR_LOCK_CHANGE, this.handleSidebarLockChange);
      this.$root.$off(EXPAND_CHANGE, this.handleExpandChange);
      this.addListListenerFlag = false;
    }
  }
};
</script>
<style lang="scss">
@import "../../style/mixins/index";
.x-table {
  &:focus {
    outline: none;
  }
  .vxe-footer--row{
    background-color: $table-header-background-color;
  }
  /*与el-checkbox的边框保持一致*/
  .vxe-cell--checkbox {
    .vxe-checkbox--icon {
      &::before{
        border-width: 1px;
      }
    }
  }
  /*.vxe-table--header {
    .vxe-header--row:not(:only-of-type) {
      .vxe-header--column {
        padding: 1px 0;
      }
    }
  }*/
}
</style>